import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import {
  Input as InputIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';

export default function CheckIn() {
  const navigate = useNavigate();
  const { inventory, inventoryTransactions, checkinInventoryItemFromPickList } = useData();
  const [checkInItems, setCheckInItems] = useState([]);
  const [itemsByPickList, setItemsByPickList] = useState({});
  const [viewPickListDialog, setViewPickListDialog] = useState({ open: false, pickListNumber: null, items: [] });
  const [checkInDialog, setCheckInDialog] = useState({ 
    open: false, 
    item: null, 
    quantity: 1, 
    maxQuantity: 0, 
    pickListNumber: null 
  });

  // Calculate items that are checked out via pick lists, grouped by pick list number
  useEffect(() => {
    const checkedOutByPickList = {};
    
    inventory.forEach(item => {
      const pickListCheckouts = inventoryTransactions.filter(
        t => t.itemId === item.id && t.jobId === 'pick-list' && t.type === 'checkout' && t.pickListNumber
      );
      const pickListCheckins = inventoryTransactions.filter(
        t => t.itemId === item.id && t.jobId === 'pick-list' && t.type === 'checkin' && t.pickListNumber
      );
      
      // Group by pick list number
      pickListCheckouts.forEach(checkout => {
        const plNumber = checkout.pickListNumber || 'Unknown';
        if (!checkedOutByPickList[plNumber]) {
          checkedOutByPickList[plNumber] = [];
        }
        
        // Calculate net checked out for this item on this pick list
        const checkinsForThisPL = pickListCheckins
          .filter(cin => cin.pickListNumber === plNumber)
          .reduce((sum, cin) => sum + (cin.quantity || 0), 0);
        const netCheckedOut = (checkout.quantity || 0) - checkinsForThisPL;
        
        if (netCheckedOut > 0) {
          const existing = checkedOutByPickList[plNumber].find(entry => entry.item.id === item.id);
          if (existing) {
            existing.checkedOutQty += netCheckedOut;
          } else {
            checkedOutByPickList[plNumber].push({
              item,
              checkedOutQty: netCheckedOut,
              pickListNumber: plNumber
            });
          }
        }
      });
    });
    
    // Store grouped by pick list for easy access
    setItemsByPickList(checkedOutByPickList);
    
    // Flatten and sort by pick list number
    const flattened = Object.entries(checkedOutByPickList).flatMap(([plNumber, items]) =>
      items.map(item => ({ ...item, pickListNumber: plNumber }))
    );
    
    setCheckInItems(flattened);
  }, [inventory, inventoryTransactions]);

  const handleViewPickList = (pickListNumber) => {
    const items = itemsByPickList[pickListNumber] || [];
    
    // Get all original checkout transactions for this pick list to see what was originally checked out
    const allCheckouts = inventoryTransactions.filter(
      t => t.jobId === 'pick-list' && t.pickListNumber === pickListNumber && t.type === 'checkout'
    );
    
    // Get all checkins for this pick list
    const allCheckins = inventoryTransactions.filter(
      t => t.jobId === 'pick-list' && t.pickListNumber === pickListNumber && t.type === 'checkin'
    );
    
    // Build complete list of items that were on this pick list
    const pickListItems = {};
    allCheckouts.forEach(checkout => {
      const item = inventory.find(i => i.id === checkout.itemId);
      if (item) {
        if (!pickListItems[item.id]) {
          pickListItems[item.id] = {
            item,
            originalQty: 0,
            checkedInQty: 0,
            stillCheckedOut: 0
          };
        }
        pickListItems[item.id].originalQty += checkout.quantity || 0;
      }
    });
    
    allCheckins.forEach(checkin => {
      if (pickListItems[checkin.itemId]) {
        pickListItems[checkin.itemId].checkedInQty += checkin.quantity || 0;
      }
    });
    
    Object.values(pickListItems).forEach(entry => {
      entry.stillCheckedOut = entry.originalQty - entry.checkedInQty;
    });
    
    setViewPickListDialog({
      open: true,
      pickListNumber,
      items: Object.values(pickListItems)
    });
  };

  const handleCheckIn = (checkedOutItem) => {
    setCheckInDialog({
      open: true,
      item: checkedOutItem.item,
      quantity: 1,
      maxQuantity: checkedOutItem.checkedOutQty,
      pickListNumber: checkedOutItem.pickListNumber
    });
  };

  const confirmCheckIn = async () => {
    try {
      if (checkInDialog.quantity > 0 && checkInDialog.quantity <= checkInDialog.maxQuantity) {
        await checkinInventoryItemFromPickList(
          checkInDialog.item.id,
          checkInDialog.quantity,
          `Checked in ${checkInDialog.quantity} ${checkInDialog.item.unit || 'units'} from ${checkInDialog.pickListNumber || 'pick list'}`,
          checkInDialog.pickListNumber
        );
        setCheckInDialog({ open: false, item: null, quantity: 1, maxQuantity: 0, pickListNumber: null });
        alert(`Successfully checked in ${checkInDialog.quantity} ${checkInDialog.item.unit || 'units'} of ${checkInDialog.item.name} from ${checkInDialog.pickListNumber || 'pick list'}!`);
      }
    } catch (error) {
      console.error('Error checking in item:', error);
      alert(error.message || 'Failed to check in item. Please try again.');
    }
  };

  const handleReturnAll = async (pickListNumber, items) => {
    if (!window.confirm(`Are you sure you want to check in ALL remaining items from Pick List ${pickListNumber}? This will check in ${items.length} item${items.length !== 1 ? 's' : ''}.`)) {
      return;
    }

    try {
      const checkInPromises = items.map(({ item, checkedOutQty }) =>
        checkinInventoryItemFromPickList(
          item.id,
          checkedOutQty,
          `Returned all items from ${pickListNumber}`,
          pickListNumber
        )
      );

      await Promise.all(checkInPromises);
      alert(`Successfully checked in all items from Pick List ${pickListNumber}!`);
    } catch (error) {
      console.error('Error checking in all items:', error);
      alert(error.message || 'Failed to check in some items. Please try again.');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 1, sm: 2 }, 
        mb: 3 
      }}>
        <IconButton onClick={() => navigate('/inventory')}>
          <ArrowBackIcon />
        </IconButton>
        <InputIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Check Items In
        </Typography>
      </Box>

      {checkInItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <InputIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Items to Check In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            All items have been checked back in, or no items are currently checked out.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/inventory')}
          >
            Back to Inventory
          </Button>
        </Paper>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {checkInItems.length} item{checkInItems.length !== 1 ? 's' : ''} currently checked out across {Object.keys(itemsByPickList).length} pick list{Object.keys(itemsByPickList).length !== 1 ? 's' : ''}. 
            When items are returned, check them back in here.
          </Alert>

          {/* Group by Pick List Number */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(itemsByPickList).sort(([a], [b]) => a.localeCompare(b)).map(([pickListNumber, items]) => (
              <Paper 
                key={pickListNumber}
                sx={{ 
                  p: 2,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Pick List {pickListNumber}
                    </Typography>
                    <Chip 
                      label={`${items.length} item${items.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewPickList(pickListNumber)}
                      size="small"
                    >
                      View All Items
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<InputIcon />}
                      onClick={() => handleReturnAll(pickListNumber, items)}
                      size="small"
                    >
                      Return All
                    </Button>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {items.map(({ item, checkedOutQty }) => (
                    <Paper 
                      key={`${item.id}-${pickListNumber}`}
                      sx={{ 
                        p: 1.5, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.location && `Location: ${item.location} • `}
                          SKU: {item.sku || 'N/A'} • 
                          <strong> {checkedOutQty}</strong> {item.unit || 'units'} checked out
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<InputIcon />}
                        onClick={() => handleCheckIn({ item, checkedOutQty, pickListNumber })}
                        sx={{ ml: 2 }}
                        size="small"
                      >
                        Check In
                      </Button>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* View Pick List Dialog */}
      <Dialog
        open={viewPickListDialog.open}
        onClose={() => setViewPickListDialog({ open: false, pickListNumber: null, items: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">
              Pick List {viewPickListDialog.pickListNumber} - All Items
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {viewPickListDialog.items.length === 0 ? (
              <Typography color="text.secondary">No items found for this pick list.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {viewPickListDialog.items.map(({ item, originalQty, checkedInQty, stillCheckedOut }) => (
                  <Paper key={item.id} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.location && `Location: ${item.location} • `}
                          SKU: {item.sku || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                      <Chip 
                        label={`Original: ${originalQty} ${item.unit || 'units'}`}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                      {checkedInQty > 0 && (
                        <Chip 
                          label={`Checked In: ${checkedInQty} ${item.unit || 'units'}`}
                          size="small"
                          color="success"
                        />
                      )}
                      <Chip 
                        label={`Still Out: ${stillCheckedOut} ${item.unit || 'units'}`}
                        size="small"
                        color={stillCheckedOut > 0 ? 'warning' : 'success'}
                        variant={stillCheckedOut > 0 ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPickListDialog({ open: false, pickListNumber: null, items: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={checkInDialog.open}
        onClose={() => setCheckInDialog({ open: false, item: null, quantity: 1, maxQuantity: 0, pickListNumber: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Check Item Back In</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {checkInDialog.item?.name}
              </Typography>
              {checkInDialog.pickListNumber && (
                <Chip 
                  label={`Pick List: ${checkInDialog.pickListNumber}`} 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            {checkInDialog.item?.location && (
              <Typography variant="body2" color="text.secondary">
                Location: {checkInDialog.item.location}
              </Typography>
            )}
            <Alert severity="info" sx={{ mt: 1 }}>
              You can check in up to <strong>{checkInDialog.maxQuantity}</strong> {checkInDialog.item?.unit || 'units'}
            </Alert>
            <TextField
              fullWidth
              label="Quantity to Check In"
              type="number"
              value={checkInDialog.quantity}
              onChange={(e) => {
                const qty = parseFloat(e.target.value) || 0;
                setCheckInDialog(prev => ({ 
                  ...prev, 
                  quantity: Math.min(qty, prev.maxQuantity) 
                }));
              }}
              inputProps={{ min: 1, max: checkInDialog.maxQuantity, step: 1 }}
              helperText={`Maximum: ${checkInDialog.maxQuantity} ${checkInDialog.item?.unit || 'units'}`}
              autoFocus
            />
            {checkInDialog.quantity > checkInDialog.maxQuantity && (
              <Alert severity="error">
                Cannot check in more than what's checked out
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInDialog({ open: false, item: null, quantity: 1, maxQuantity: 0, pickListNumber: null })}>
            Cancel
          </Button>
          <Button
            onClick={confirmCheckIn}
            variant="contained"
            color="success"
            disabled={checkInDialog.quantity <= 0 || checkInDialog.quantity > checkInDialog.maxQuantity}
            startIcon={<InputIcon />}
          >
            Check In
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

